import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { map } from 'rxjs';
import { AuthFacade } from '../auth.facade';
import { SessionCheckerService } from '../../services/session-checker.service';
import { LoggingService } from '../../services/logging.service';

/**
 * Functional guard that protects routes requiring an authenticated user.
 *
 * Behavior:
 * 1. If not authenticated locally -> redirect to /auth-error.
 * 2. If authenticated -> verify active IdP session (Ping or Okta via SessionCheckerService).
 * 3. If session inactive -> redirect to /auth-error.
 */
export const authGuard: CanActivateFn = () => {
  const authFacade = inject(AuthFacade);
  const router = inject(Router);
  const sessionChecker = inject(SessionCheckerService);
  const logger = inject(LoggingService);

  if (!authFacade.isAuthenticated()) {
    logger.warn('[authGuard] User not authenticated. Redirecting to /auth-error.');
    return router.parseUrl('/auth-error');
  }

  return sessionChecker.isSessionActive().pipe(
    map((isActive) => {
      if (!isActive) {
        logger.warn('[authGuard] Remote session inactive. Redirecting to /auth-error.');
        return router.parseUrl('/auth-error');
      }
      return true;
    }),
  );
};
